import { countOf, eventTag, heading, line } from "./shared";
import { type JournalRecord, formatWorldDate, provenanceOf } from "../../journal/journal";
import { touchCloseHint } from "../../input/touch";
import { type EventId, type WorldDateView } from "../../world/types";

/*
 * The trace view: the journal read by record instead of by entry (chunk U-0's
 * split of `panels.ts`).
 *
 * The only development-only surface in this directory, and the other half of
 * the debug hook every panel carries: a tag says *which* record, and this says
 * what the record was and how many different ways it reached the player. Phase
 * 3's exit criterion is traceability, and a channel that cannot say which
 * record it is about cannot be checked.
 *
 * `main.ts` binds its key behind `import.meta.env.DEV`; the class itself is
 * ordinary DOM and costs a hidden `<div>` in a bundle that never opens it.
 */

/**
 * One record as the trace view shows it: the log's own sentence for it, and
 * every entry the player filed about it.
 *
 * Assembled by `witness/witness.ts` from {@link JournalRecord} plus
 * `WitnessSource.describe`, because turning an `EventId` back into the
 * chronicler's line is a query and a panel renders strings (the same split
 * {@link MarketPanel.show}'s `trace` argument makes).
 */
export interface TracedRecord extends JournalRecord {
  /** `HistoryEvent::narrate`. Never diegetic text — this panel is the debug half. */
  readonly narration: string;
}

/**
 * One beat the director staged, as the trace view shows it.
 *
 * **Phase 4's cross-cutting rule, made checkable**: "every staged beat carries
 * its `EventId`s and the trace view shows them". It is a separate list from
 * {@link TracedRecord} because it answers a separate question — that one is what
 * the player was *told*, and this is what the client chose to put in front of
 * them. The two usually overlap and the interesting cases are where they do not:
 * a reaction whose criers all answered `null` files nothing in the journal and
 * still appears here, with the record it was about and the day it was allowed to
 * surface.
 */
export interface StagedDelivery {
  /** `SpotlightBeat.id`, which is unique across settlements and stable. */
  readonly id: number;

  /** One of `world/types.ts`'s `BEAT_*` surfaces. */
  readonly channel: number;

  /** Where the player was standing, and the day it was staged. */
  readonly where: string;
  readonly when: WorldDateView;

  /** The earliest the simulation said it could surface. Never after {@link when}. */
  readonly earliest: WorldDateView;

  /** The records it carried, with the log's own sentence for each. */
  readonly records: readonly { readonly event: EventId; readonly narration: string }[];
}

/**
 * One age as the trace view shows it: the book's own lines, and the log's
 * sentence for every record each of them cites.
 *
 * **Phase 6's exit criterion in one shape** — *"every line traceable in the
 * debug view to log records; nothing invented"*. It is a third list beside
 * {@link TracedRecord} and {@link StagedDelivery} because it answers a third
 * question: those two are what the player was told and what the client chose to
 * tell them, and this is what the *world* says about itself when nobody is being
 * spoken to at all.
 *
 * Both of an age's names are carried whatever the book's page leads with, since
 * which of them it should lead with is an open taste call (`witness/saga.ts`'s
 * `ERA_HEADING`) and a tester judging the two wants them side by side.
 */
export interface TracedEra {
  /** Which age it is, counting from one, and its two names. */
  readonly ordinal: number;
  readonly title: string;
  readonly name: string;

  /** The days it began and ended. `null` for an age still running, or unsaid. */
  readonly began: WorldDateView | null;
  readonly ended: WorldDateView | null;

  /** Every line of the age, with the records behind it. Never an empty citation list. */
  readonly lines: readonly {
    readonly text: string;
    readonly cites: readonly { readonly event: EventId; readonly narration: string }[];
  }[];
}

/**
 * The life the player was born into, as the trace view shows it.
 *
 * **The origins screen's half of the traceability rule** (Phase 6, wave 3C): a
 * card is a diegetic surface and carries no record tags, so the ids it stands on
 * are one keypress away instead — the record behind the life, and the record
 * behind every seat the card said the player's parents held.
 *
 * `null` in every session nobody was born in, which is a declined birth screen, a
 * `?born=0` link, and every session before this phase.
 */
export interface TracedOrigin {
  /** Which door: `orphan`, `unrecorded`, `bound` (`history::origins::OriginKind`). */
  readonly kind: string;

  /** The card's own line, as the player read it before they took it. */
  readonly line: string;

  /** What they typed. Empty when the birth was declined at the name. */
  readonly name: string;

  /** Whether the command was queued, or the build had no door to queue it through. */
  readonly queued: boolean;

  /** The record behind the life, and the log's own sentence for it. */
  readonly cites: readonly { readonly event: EventId; readonly narration: string }[];

  /** Each seat the card claimed, with the record that seated the parent. */
  readonly claims: readonly {
    readonly line: string;
    readonly cites: readonly { readonly event: EventId; readonly narration: string }[];
  }[];
}

/**
 * The trace view: every record the player has learned, and how many separate
 * ways it reached them.
 *
 * **This is the exit criterion, drawn.** The roadmap asks that three independent
 * diegetic channels carry news of the same war "each traceable in debug view to
 * the same event-log entries", and the journal already holds exactly that — an
 * `EventId` per entry with the channel that delivered it (plan decision 10). All
 * this does is group the entries by record, put the log's own sentence at the
 * top of each group, and count the distinct channels underneath, so the claim is
 * read rather than reconstructed.
 *
 * Development only, by binding rather than by construction: it shows nothing a
 * player would understand and everything a tester needs, which is the same trade
 * `dev/handle.ts` makes.
 */
export class TracePanel {
  private readonly element: HTMLElement;
  private readonly bodyElement: HTMLElement;
  private readonly countElement: HTMLElement;

  /**
   * Who the simulation is composing every spoken line *to*.
   *
   * Phase 5 decision 6a's debug half. The listener is set once, sim-side, and
   * the client neither passes it nor can turn it off (`world/types.ts`) — so a
   * line that comes back in the second person is a line about a record that
   * names this figure, and the only thing a debug surface can do about it is
   * say who the figure is. Empty before a name is claimed, which is also the
   * honest reading of "nobody is being spoken to".
   */
  private readonly listenerElement: HTMLElement;

  constructor(root: HTMLElement, touch = false) {
    this.element = document.createElement("div");
    this.element.className = "hud__panel hud__panel--trace";
    this.element.hidden = true;

    const title = document.createElement("div");
    title.className = "hud__panel-title";
    title.textContent = "trace — what the log says about what you were told";

    this.countElement = document.createElement("div");
    this.countElement.className = "hud__panel-count";

    this.listenerElement = document.createElement("div");
    this.listenerElement.className = "hud__panel-count";
    this.listenerElement.hidden = true;

    this.bodyElement = document.createElement("div");
    this.bodyElement.className = "hud__panel-body";

    const hint = document.createElement("div");
    hint.className = "hud__panel-hint";
    hint.textContent = touch ? touchCloseHint("trace") : "T closes";

    this.element.append(title, this.countElement, this.listenerElement, this.bodyElement, hint);
    root.append(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /**
   * Rebuilds both lists and shows the panel.
   *
   * `listener` is the figure every spoken line is now composed to — its handle
   * and the simulation's own introduction of it, or `null` before a claim. It
   * sits under the count rather than in the list because it is not a record: it
   * is the lens the records are being read through.
   */
  show(
    records: readonly TracedRecord[],
    staged: readonly StagedDelivery[] = [],
    listener: { readonly figure: number; readonly introduced: string } | null = null,
    saga: readonly TracedEra[] = [],
    origin: TracedOrigin | null = null,
  ): void {
    const carried = records.filter((record) => channelsOf(record).length >= 3).length;

    this.countElement.textContent =
      records.length === 0
        ? ""
        : `${records.length} record${records.length === 1 ? "" : "s"}` +
          ` · ${carried} carried by three channels or more`;
    this.listenerElement.hidden = listener === null;
    // The handle first and the sentence after it, which is this panel's own
    // habit ({@link renderTrace}): a tester checks the lens by its number, and
    // the introduction is what tells them which figure that number is. The
    // introduction is first-person because `speech::introduce` is what a figure
    // would *say* — it is quoted here rather than rewritten, like every other
    // sentence on this screen.
    this.listenerElement.textContent =
      listener === null
        ? ""
        : `every line is composed to figure ${eventTag(listener.figure)} — “${listener.introduced}”`;
    this.bodyElement.replaceChildren(
      // The life first, above everything the player was told, because it is the
      // one thing on this screen that happened before the session did.
      ...(origin === null ? [] : [heading("the life this session was born into"), renderOrigin(origin)]),
      ...(records.length === 0
        ? [line("nothing traced yet — every journal entry appears here with its record")]
        : records.map((record) => renderTrace(record))),
      ...(staged.length === 0
        ? []
        : [
            heading(`the director staged ${staged.length} beat${staged.length === 1 ? "" : "s"}`),
            ...staged.map((delivery) => renderStaged(delivery)),
          ]),
      // The world's own book, under what the player was told: it is the longest
      // section and the one nobody was told anything by, so it reads last.
      ...(saga.length === 0
        ? []
        : [
            heading(
              `the book of eras — ${countOf(saga.length, "age")},` +
                ` ${countOf(sagaLines(saga), "line")}, every one of them cited`,
            ),
            ...saga.map((era) => renderEra(era)),
          ]),
    );
    this.element.hidden = false;
  }

  hide(): void {
    this.element.hidden = true;
  }
}

/** One traced record: its id, the log's sentence, and every way it arrived. */
function renderTrace(record: TracedRecord): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__trace-record";

  const narration = document.createElement("div");
  narration.className = "hud__trace-narration";
  // The id first and the sentence after it, so a column of these scans by id:
  // the exit criterion is checked by comparing ids between channels, and the
  // sentence is what tells the tester which war they are looking at.
  narration.textContent = `${eventTag(record.event)}  ${record.narration}`;

  const channels = channelsOf(record);
  const count = document.createElement("div");
  count.className = "hud__trace-channels";
  count.textContent = `carried by ${channels.length} channel${channels.length === 1 ? "" : "s"}: ${channels.join(", ")}`;

  element.append(narration, count);

  for (const entry of record.entries) {
    const via = document.createElement("div");
    via.className = "hud__trace-via";
    via.textContent = provenanceOf(entry);
    element.append(via);
  }

  return element;
}

/** The distinct channels one record arrived through. Two graves are still one channel. */
function channelsOf(record: TracedRecord): readonly string[] {
  const seen: string[] = [];

  for (const entry of record.entries) {
    if (!seen.includes(entry.via.channel)) {
      seen.push(entry.via.channel);
    }
  }

  return seen;
}

/**
 * One beat the director staged: what it was, where and when, and every record
 * behind it.
 *
 * The staging date and the earliest honest date are printed side by side even
 * when they are the same day, because "never before its record" is the claim
 * this list exists to let somebody check by eye.
 */
function renderStaged(delivery: StagedDelivery): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__trace-record";

  const title = document.createElement("div");
  title.className = "hud__trace-narration";
  title.textContent =
    `beat ${delivery.id} · ${surfaceName(delivery.channel)} · staged in ${delivery.where},` +
    ` ${formatWorldDate(delivery.when)}`;

  const dates = document.createElement("div");
  dates.className = "hud__trace-channels";
  dates.textContent = `earliest it could surface: ${formatWorldDate(delivery.earliest)}`;

  element.append(title, dates);

  for (const record of delivery.records) {
    const cited = document.createElement("div");
    cited.className = "hud__trace-via";
    cited.textContent = `${eventTag(record.event)}  ${record.narration}`;
    element.append(cited);
  }

  return element;
}

/**
 * One age of the book: both its names, the days it ran, and every line under the
 * records it narrates.
 *
 * The line at full strength and its citations dimmed underneath, which is this
 * panel's own shape for "the claim, and what it rests on" ({@link renderStaged}
 * draws a staged beat the same way). A reader checking *"nothing invented"* is
 * comparing two things per row, and they are the two things on the row.
 */
function renderEra(era: TracedEra): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__trace-record";

  const title = document.createElement("div");
  title.className = "hud__trace-narration";
  // The ordinal first and both names after it. The number is not the row's
  // position and is worth printing for exactly that reason: a book cut to its
  // last few ages opens on *the fourth age* at index zero, so a tester counting
  // rows and a book counting ages disagree by design (`world/types.ts`'s
  // `SagaEra.ordinal`). Both names, because which one a page should lead with is
  // the taste call this list exists to inform (`witness/saga.ts`'s
  // `ERA_HEADING`).
  title.textContent = `age ${era.ordinal} · ${era.title} · ${era.name}`;

  const dates = document.createElement("div");
  dates.className = "hud__trace-channels";
  // An age still running says so rather than showing a blank: "the one the
  // reader is standing in" is the interesting case, not a missing field.
  dates.textContent =
    `${era.began === null ? "?" : formatWorldDate(era.began)} — ` +
    `${era.ended === null ? "still running" : formatWorldDate(era.ended)}`;

  element.append(title, dates);

  for (const line of era.lines) {
    const written = document.createElement("div");
    written.className = "hud__trace-narration";
    written.textContent = line.text;
    element.append(written);

    for (const cite of line.cites) {
      const cited = document.createElement("div");
      cited.className = "hud__trace-via";
      cited.textContent = `${eventTag(cite.event)}  ${cite.narration}`;
      element.append(cited);
    }
  }

  return element;
}

/**
 * The life the session was born into: the card as it was read, and the records
 * under it.
 *
 * {@link renderEra}'s shape exactly — the sentence at full strength, its
 * citations dimmed beneath — because a reader is checking the same thing about
 * both: that what the world said about itself rests on records the log holds.
 * The claims are the interesting rows here, since a claim is the one line on a
 * card that is *derived* rather than read off a record
 * (`witness/origins.ts`'s `claimOf`).
 *
 * Whether the command was queued is printed rather than assumed. A birth the
 * simulation refused looks exactly like a birth it took from up here — a tick has
 * no error channel — so the only honest thing this row can say is what the client
 * sent, and `playerFigure()` is where a tester goes to find out what became of it.
 */
function renderOrigin(origin: TracedOrigin): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__trace-record";

  const said = document.createElement("div");
  said.className = "hud__trace-narration";
  said.textContent = origin.line;

  const how = document.createElement("div");
  how.className = "hud__trace-channels";
  how.textContent =
    `${origin.kind} · ${origin.name === "" ? "no name typed" : `named “${origin.name}”`}` +
    ` · ${origin.queued ? "queued as a command" : "not queued — this build has no birth door"}`;

  element.append(said, how);

  for (const cite of origin.cites) {
    const cited = document.createElement("div");
    cited.className = "hud__trace-via";
    cited.textContent = `${eventTag(cite.event)}  ${cite.narration}`;
    element.append(cited);
  }

  for (const claim of origin.claims) {
    const held = document.createElement("div");
    held.className = "hud__trace-narration";
    held.textContent = claim.line;
    element.append(held);

    for (const cite of claim.cites) {
      const cited = document.createElement("div");
      cited.className = "hud__trace-via";
      cited.textContent = `${eventTag(cite.event)}  ${cite.narration}`;
      element.append(cited);
    }
  }

  return element;
}

/** How many lines the whole book holds, for the section's heading. */
function sagaLines(saga: readonly TracedEra[]): number {
  return saga.reduce((total, era) => total + era.lines.length, 0);
}

/**
 * What the client calls a beat surface, for the debug list.
 *
 * The four `BEAT_*` ids of `world/types.ts`, which `world/wasm.ts` checks
 * against the simulation's own names at startup — so this is a lookup rather
 * than a claim, and a build where the two disagree never gets this far.
 */
function surfaceName(channel: number): string {
  return ["road", "board", "mouth", "ground"][channel] ?? `surface ${channel}`;
}
